// File: src/pages/SalesOrderReport.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Label,
  Alert,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ImDownload } from "react-icons/im";
import { FaFilter, FaChevronRight, FaChevronDown } from "react-icons/fa";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";
import { pdf } from "@react-pdf/renderer";

// ---- All shared deps come from ../import/ as requested ----
import SuperAdminUI from "../import/SuperAdminUI";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";

// API layer
import {
  _Get,
  _GetById,
  VeiwLedgerBalance,
} from "../import/ApiEndPoint/ApiCalling";
import {
  view_create_order_history,
  View_Invoice_Data_by_id,
} from "../import/ApiEndPoint/Api";

// Printable invoice component
import LastestInvoice from "./LastestInvoice";

/* -------------------- tiny helpers -------------------- */
const S = (v) => (v == null ? "" : String(v));
const N = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const firstNum = (...vals) => {
  for (const v of vals) {
    if (v !== null && v !== undefined && v !== "" && !Number.isNaN(Number(v))) {
      return Number(v);
    }
  }
  return null;
};

const fmtDateTime = (s) => {
  if (!s) return "—";
  try {
    const d = new Date(s);
    return d.toLocaleString("en-IN", { hour12: true });
  } catch {
    return String(s);
  }
};
const fmtDateOnly = (s) => {
  if (!s) return "-";
  try {
    return String(s).split("T")[0];
  } catch {
    return "-";
  }
};
const statusOf = (o) =>
  S(o?.status ?? o?.orderStatus ?? o?.state ?? o?.currentStatus)
    .trim()
    .toLowerCase();

const getInvoiceNo = (r, prefix = "", suffix = "") => {
  const inv =
    r?.invoiceId ??
    r?.InvoiceId ??
    r?.invoiceNo ??
    r?.Invoice_No ??
    r?.SalesInvoiceNo ??
    r?.order?.invoiceId ??
    "";
  return inv ? `${prefix}${inv}${suffix}` : "-";
};

/* -------------------- FY helpers -------------------- */
const MONTHS_FY = [
  { key: 4, label: "April" },
  { key: 5, label: "May" },
  { key: 6, label: "June" },
  { key: 7, label: "July" },
  { key: 8, label: "August" },
  { key: 9, label: "September" },
  { key: 10, label: "October" },
  { key: 11, label: "November" },
  { key: 12, label: "December" },
  { key: 1, label: "January" },
  { key: 2, label: "February" },
  { key: 3, label: "March" },
];

const QUARTERS = [
  { value: "Q1", label: "Q1 (Apr - Jun)", months: [4, 5, 6] },
  { value: "Q2", label: "Q2 (Jul - Sep)", months: [7, 8, 9] },
  { value: "Q3", label: "Q3 (Oct - Dec)", months: [10, 11, 12] },
  { value: "Q4", label: "Q4 (Jan - Mar)", months: [1, 2, 3] },
];

const HALVES = [
  { value: "H1", label: "H1 (Apr - Sep)", months: [4, 5, 6, 7, 8, 9] },
  { value: "H2", label: "H2 (Oct - Mar)", months: [10, 11, 12, 1, 2, 3] },
];

function getFinancialYearString(dateObj) {
  const d = new Date(dateObj);
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return m >= 4 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
}

function getFinancialYearStartYear(fy) {
  const n = Number(String(fy).split("-")[0]);
  return Number.isFinite(n) ? n : new Date().getFullYear();
}

function getFYMonthMeta(dateObj) {
  const d = new Date(dateObj);
  const month = d.getMonth() + 1;
  const year = d.getFullYear();
  const fy = getFinancialYearString(d);

  const labelMap = {
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
    1: "January",
    2: "February",
    3: "March",
  };

  const sortIndexMap = {
    4: 1,
    5: 2,
    6: 3,
    7: 4,
    8: 5,
    9: 6,
    10: 7,
    11: 8,
    12: 9,
    1: 10,
    2: 11,
    3: 12,
  };

  const fyStart = getFinancialYearStartYear(fy);
  const displayYear = month >= 4 ? fyStart : fyStart + 1;

  return {
    fy,
    month,
    label: `${labelMap[month]} ${displayYear}`,
    sortIndex: sortIndexMap[month],
    displayYear,
    realYear: year,
  };
}

const getRowDate = (r) =>
  new Date(r?._orderDateRaw || r?.date || r?.createdAt || Date.now());

/* -------------------- API-first totals patch -------------------- */
function patchTotals(order) {
  const o = JSON.parse(JSON.stringify(order || {}));
  let calcBasic = 0,
    calcCgst = 0,
    calcSgst = 0,
    calcIgst = 0;

  (o.orderItems || []).forEach((it) => {
    const qty = N(it.qty);
    const price = N(
      firstNum(
        it.BasicPrice,
        it.basicPrice,
        it.price,
        it?.productId?.SalesRate,
        it?.productId?.Product_MRP,
        0,
      ),
    );
    const lineBasic = +(
      firstNum(it.BasicTotal, it.taxableAmount, price * qty, 0) || 0
    ).toFixed(2);
    const gst = N(it.gstPercentage ?? it.gst ?? it?.productId?.GSTRate);

    let itemCgst = firstNum(it.cgstTotal, it.cgstAmount, it.CGSTTotal);
    let itemSgst = firstNum(it.sgstTotal, it.sgstAmount, it.SGSTTotal);
    let itemIgst = firstNum(it.igstTotal, it.igstAmount, it.IGSTTotal);

    if (itemCgst == null && itemSgst == null && itemIgst == null) {
      if (Number(o.igstTaxType) === 1 || o.igstTaxType === true) {
        itemIgst = +((lineBasic * gst) / 100).toFixed(2);
        itemCgst = 0;
        itemSgst = 0;
      } else {
        itemCgst = +((lineBasic * (gst / 2)) / 100).toFixed(2);
        itemSgst = +((lineBasic * (gst / 2)) / 100).toFixed(2);
        itemIgst = 0;
      }
    }

    it.igstTotal = N(itemIgst);
    it.cgstTotal = N(itemCgst);
    it.sgstTotal = N(itemSgst);
    it.BasicPrice = +price.toFixed(2);
    it.basicPrice = +price.toFixed(2);
    it.BasicTotal = lineBasic;
    it.taxableAmount =
      it.taxableAmount != null ? +N(it.taxableAmount).toFixed(2) : lineBasic;
    it.grandTotal = +(
      firstNum(
        it.grandTotal,
        lineBasic + N(itemCgst) + N(itemSgst) + N(itemIgst),
        0,
      ) || 0
    ).toFixed(2);

    calcBasic += lineBasic;
    calcCgst += N(itemCgst);
    calcSgst += N(itemSgst);
    calcIgst += N(itemIgst);
  });

  const apiAmount = firstNum(
    o?.amount,
    o?.Amount,
    o?.basicTotal,
    o?.BasicTotal,
    o?.taxableAmount,
  );
  const apiCgst = firstNum(o?.cgstTotal, o?.CGSTTotal, o?.cgst, o?.CGST);
  const apiSgst = firstNum(o?.sgstTotal, o?.SGSTTotal, o?.sgst, o?.SGST);
  const apiIgst = firstNum(o?.igstTotal, o?.IGSTTotal, o?.igst, o?.IGST);
  const apiGrand = firstNum(
    o?.grandTotal,
    o?.GrandTotal,
    o?.grand_total,
    o?.totalAmount,
    o?.TotalAmount,
  );

  o.amount = +(apiAmount != null ? apiAmount : calcBasic).toFixed(2);
  o.taxableAmount = o.amount;
  o.cgstTotal = +(apiCgst != null ? apiCgst : calcCgst).toFixed(2);
  o.sgstTotal = +(apiSgst != null ? apiSgst : calcSgst).toFixed(2);
  o.igstTotal = +(apiIgst != null ? apiIgst : calcIgst).toFixed(2);
  o.gstTotal = +(o.cgstTotal + o.sgstTotal + o.igstTotal).toFixed(2);
  o.grandTotal = +(apiGrand != null ? apiGrand : o.amount + o.gstTotal).toFixed(
    2,
  );

  return o;
}

/* -------------------- flatten helper from order-level API -------------------- */
function flattenOrdersForReport(orderList, prefix = "", suffix = "") {
  return (orderList || []).map((o) => {
    const patched = patchTotals(o);
    return {
      ...patched,
      _fullInvoice: getInvoiceNo(patched, prefix, suffix),
      _orderDateRaw: patched?.date || patched?.createdAt || "",
      _orderDateOnly: fmtDateOnly(patched?.date || patched?.createdAt),
      _salesPersonName:
        patched?.salesPersonName ||
        patched?.userId?.firstName ||
        patched?.userId?.name ||
        "-",
      _partyName: patched?.partyId?.CompanyName || patched?.fullName || "-",
      _statusLabel: patched?.status || "-",
      _basicAmount:
        patched?.amount != null
          ? N(patched.amount)
          : N(patched?.taxableAmount || 0),
      _taxAmount:
        N(patched?.cgstTotal) + N(patched?.sgstTotal) + N(patched?.igstTotal),
      _grandAmount: N(patched?.grandTotal),
    };
  });
}

/* -------------------- month grouping helpers -------------------- */
function makeTotals(rows = []) {
  return {
    cgst: rows.reduce((a, r) => a + N(r?.cgstTotal), 0),
    sgst: rows.reduce((a, r) => a + N(r?.sgstTotal), 0),
    igst: rows.reduce((a, r) => a + N(r?.igstTotal), 0),
    basic: rows.reduce((a, r) => a + N(r?._basicAmount), 0),
    tax: rows.reduce((a, r) => a + N(r?._taxAmount), 0),
    grand: rows.reduce((a, r) => a + N(r?._grandAmount), 0),
  };
}

function groupRowsByMonth(rows = []) {
  const bucket = {};
  rows.forEach((r) => {
    const meta = getFYMonthMeta(getRowDate(r));
    const key = `${meta.fy}-${meta.month}`;
    if (!bucket[key]) {
      bucket[key] = {
        key,
        label: meta.label,
        month: meta.month,
        fy: meta.fy,
        sortIndex: meta.sortIndex,
        rows: [],
      };
    }
    bucket[key].rows.push(r);
  });

  return Object.values(bucket)
    .map((g) => ({
      ...g,
      rows: g.rows.sort(
        (a, b) =>
          new Date(b?._orderDateRaw || 0) - new Date(a?._orderDateRaw || 0),
      ),
      totals: makeTotals(g.rows),
    }))
    .sort((a, b) => a.sortIndex - b.sortIndex);
}

/* -------------------- once CSS -------------------- */
const injectOnce = () => {
  if (document.getElementById("salesorder-inline-css")) return;
  const style = document.createElement("style");
  style.id = "salesorder-inline-css";
  style.innerHTML = `
    .so-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .so-table-wrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .so-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:1180px; }
    .so-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .so-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .so-table tbody tr:hover{ background:#f9fbff; }
    .so-table tfoot td{
      padding:8px 10px; font-weight:800; background:#fafafa; border-top:1px solid #e5e7eb; text-align:center;
    }
    .so-month-row{
      background:#eef6ff;
      font-weight:800;
      cursor:pointer;
    }
    .so-month-row:hover{
      background:#e4efff !important;
    }
    .so-month-name{
      display:flex;
      align-items:center;
      gap:8px;
      justify-content:flex-start;
    }
    .picklist{ border:1px solid #e5e7eb; border-radius:10px; background:#fff; padding:8px; max-height:300px; overflow:auto; }
    .pickitem{ padding:8px 10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px; background:#fff; cursor:pointer; }
    .link { color:#1d4ed8; text-decoration:underline; cursor:pointer; }
    .so-filter-card{
      padding-top:4px;
      padding-bottom:4px;
      border-radius:14px;
      border:1px solid #e5e7eb;
      background:#f4f7f6;
    }
  `;
  document.head.appendChild(style);
};

// Column defs
const BASE_COLS = [
  {
    key: "invoice",
    header: "Invoice No.",
    get: (r) => r?._fullInvoice || "-",
    render: (r, onClickInvoice) =>
      r?._fullInvoice && r?._fullInvoice !== "-" ? (
        <span
          className="link"
          title="Download Invoice PDF"
          onClick={() => onClickInvoice(r)}
        >
          {r._fullInvoice}
        </span>
      ) : (
        "-"
      ),
  },
  {
    key: "orderDate",
    header: "Order Date",
    get: (r) => r?._orderDateOnly || "-",
  },
  {
    key: "salesPerson",
    header: "Sales Person Name",
    get: (r) => r?._salesPersonName || "-",
  },
  {
    key: "party",
    header: "Party Name",
    get: (r) => r?._partyName || "-",
  },
  {
    key: "cgst",
    header: "CGST",
    get: (r) => N(r?.cgstTotal).toFixed(2),
  },
  {
    key: "sgst",
    header: "SGST",
    get: (r) => N(r?.sgstTotal).toFixed(2),
  },
  {
    key: "igst",
    header: "IGST",
    get: (r) => N(r?.igstTotal).toFixed(2),
  },
  {
    key: "basic",
    header: "Basic Amount",
    get: (r) => N(r?._basicAmount).toFixed(2),
  },
  {
    key: "tax",
    header: "Tax",
    get: (r) => N(r?._taxAmount).toFixed(2),
  },
  {
    key: "grand",
    header: "Total",
    get: (r) => N(r?._grandAmount).toFixed(2),
  },
  {
    key: "status",
    header: "Status",
    get: (r) => r?._statusLabel || "-",
    render: (r) => {
      const st = r?._statusLabel || "-";
      const s = String(st).toLowerCase();
      let color = "secondary";
      if (s.includes("completed")) color = "success";
      else if (s.includes("pending")) color = "warning";
      else if (s.includes("cancel")) color = "danger";
      else if (s.includes("dispatch")) color = "info";
      return <Badge color={color}>{st}</Badge>;
    },
  },
];

export default function SalesOrderReport() {
  const navigate = useNavigate();
  const addBufferRef = useRef([]);

  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [fy, setFy] = useState("");
  const [periodType, setPeriodType] = useState("YEARLY");
  const [selectedMonth, setSelectedMonth] = useState(4);
  const [selectedQuarter, setSelectedQuarter] = useState("Q1");
  const [selectedHalf, setSelectedHalf] = useState("H1");

  const [monthExpanded, setMonthExpanded] = useState({});

  const [masterShow, setMasterShow] = useState(false);

  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  useEffect(() => injectOnce(), []);

  const loadData = async (overrideUser = null) => {
    setLoading(true);
    setErrorMsg("");
    try {
      const user =
        overrideUser || JSON.parse(localStorage.getItem("userData") || "{}");
      if (user?.rolename?.roleName === "MASTER") setMasterShow(true);

      const inside = CheckPermission("SalesComplete Order Report");
      if (inside && typeof inside === "object") {
        setPermissions((p) => ({ ...p, ...inside }));
      }

      const uid = user?._id || user?.id || "";
      const db = user?.database || user?.db || "";

      const res = await _Get(view_create_order_history, uid, db);
      const list = Array.isArray(res?.orderHistory) ? res.orderHistory : [];

      const company = JSON.parse(localStorage.getItem("Companydetail") || "{}");
      const prefix = company?.Prefix || "";
      const suffix = company?.Suffix || "";

      const normalized = flattenOrdersForReport(list, prefix, suffix);

      normalized.sort((a, b) => {
        const da = new Date(a?._orderDateRaw || 0).getTime();
        const dbt = new Date(b?._orderDateRaw || 0).getTime();
        if (dbt !== da) return dbt - da;

        const ia = parseInt(
          a?.invoiceId || a?.order?.invoiceId || a?.orderNo || 0,
          10,
        );
        const ib = parseInt(
          b?.invoiceId || b?.order?.invoiceId || b?.orderNo || 0,
          10,
        );
        return (isNaN(ib) ? 0 : ib) - (isNaN(ia) ? 0 : ia);
      });

      setRows(normalized);
      setAllRows(normalized);

      const fySet = [
        ...new Set(
          normalized.map((r) => getFinancialYearString(getRowDate(r))),
        ),
      ].sort((a, b) => Number(b.split("-")[0]) - Number(a.split("-")[0]));
      setFy(fySet[0] || getFinancialYearString(new Date()));

      const saved = localStorage.getItem("salesorderreports");
      let keys = BASE_COLS.map((c) => c.key);
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          const candidate = Array.isArray(parsed)
            ? parsed
                .map((p) => p?.key)
                .filter((k) => BASE_COLS.some((b) => b.key === k))
            : [];
          if (candidate.length) keys = candidate;
        } catch {}
      }
      setSelectedKeys(keys);
    } catch (e) {
      console.error("[SalesOrderReport] load error:", e);
      setErrorMsg(
        e?.response?.data?.message || e?.message || "Failed to load data.",
      );
      setRows([]);
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const fyOptions = useMemo(() => {
    const fySet = [
      ...new Set(allRows.map((r) => getFinancialYearString(getRowDate(r)))),
    ].sort((a, b) => Number(b.split("-")[0]) - Number(a.split("-")[0]));
    return fySet;
  }, [allRows]);

  const colMap = useMemo(() => {
    const m = new Map();
    BASE_COLS.forEach((c) => m.set(c.key, c));
    return m;
  }, []);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap],
  );

  const filteredRows = useMemo(() => {
    let data = allRows.slice();

    if (fy) {
      data = data.filter((r) => getFinancialYearString(getRowDate(r)) === fy);
    }

    if (periodType === "MONTHLY") {
      data = data.filter(
        (r) => getFYMonthMeta(getRowDate(r)).month === Number(selectedMonth),
      );
    } else if (periodType === "QUARTERLY") {
      const q = QUARTERS.find((x) => x.value === selectedQuarter);
      const months = q?.months || [];
      data = data.filter((r) =>
        months.includes(getFYMonthMeta(getRowDate(r)).month),
      );
    } else if (periodType === "HALF_YEARLY") {
      const h = HALVES.find((x) => x.value === selectedHalf);
      const months = h?.months || [];
      data = data.filter((r) =>
        months.includes(getFYMonthMeta(getRowDate(r)).month),
      );
    }

    if (statusFilter && statusFilter !== "all") {
      const term = statusFilter.toLowerCase();

      data = data.filter((r) => {
        const st = statusOf(r);

        if (term === "pending") return st.includes("pending");
        if (term === "billing") return /billing|forward/.test(st);
        if (term === "dispatch")
          return /dispatch|ready\s*to\s*dispatch|delivery boy assigned/.test(
            st,
          );
        if (term === "pending for delivery") return st === "out for delivery";
        if (term === "completed") return st.includes("completed");
        if (term === "cancel in process")
          return st.includes("cancel in process");
        if (term === "cancelled") return st.includes("cancel");
        return st === term;
      });
    }

    const term = search.trim().toLowerCase();
    if (!term) return data;

    return data.filter((r) => {
      const hay = [
        r?._fullInvoice,
        r?._salesPersonName,
        r?._partyName,
        r?._statusLabel,
        r?._basicAmount,
        r?._grandAmount,
        r?.orderNo,
        r?.invoiceId,
      ]
        .map(S)
        .join(" ")
        .toLowerCase();

      return hay.includes(term);
    });
  }, [
    allRows,
    fy,
    periodType,
    selectedMonth,
    selectedQuarter,
    selectedHalf,
    statusFilter,
    search,
  ]);

  useEffect(() => {
    setRows(filteredRows);
  }, [filteredRows]);

  const groupedRows = useMemo(
    () => groupRowsByMonth(filteredRows),
    [filteredRows],
  );

  useEffect(() => {
    const initial = {};
    groupedRows.forEach((g) => {
      initial[g.key] = false; // collapsed by default
    });
    setMonthExpanded(initial);
  }, [groupedRows]);

  const totals = useMemo(() => makeTotals(filteredRows), [filteredRows]);

  const toggleMonth = (key) => {
    setMonthExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, key) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    const toPersist = selectedKeys.map((k) => ({
      key: k,
      header: colMap.get(k)?.header || k,
    }));
    localStorage.setItem("salesorderreports", JSON.stringify(toPersist));
    closeModal();
  };

  const flatRow = (r) => ({
    "Invoice No.": r?._fullInvoice || "",
    "Order Date": r?._orderDateOnly || "-",
    "Sales Person Name": r?._salesPersonName || "-",
    "Party Name": r?._partyName || "-",
    CGST: N(r?.cgstTotal).toFixed(2),
    SGST: N(r?.sgstTotal).toFixed(2),
    IGST: N(r?.igstTotal).toFixed(2),
    "Basic Amount": N(r?._basicAmount).toFixed(2),
    Tax: N(r?._taxAmount).toFixed(2),
    Total: N(r?._grandAmount).toFixed(2),
    Status: r?._statusLabel || "-",
  });

  const exportXLSX = (fileName = "SalesOrderReport") => {
    const data = [];
    groupedRows.forEach((group) => {
      data.push({
        "Invoice No.": `${group.label} TOTAL`,
        "Order Date": "",
        "Sales Person Name": "",
        "Party Name": "",
        CGST: group.totals.cgst.toFixed(2),
        SGST: group.totals.sgst.toFixed(2),
        IGST: group.totals.igst.toFixed(2),
        "Basic Amount": group.totals.basic.toFixed(2),
        Tax: group.totals.tax.toFixed(2),
        Total: group.totals.grand.toFixed(2),
        Status: "",
      });
      group.rows.forEach((r) => data.push(flatRow(r)));
    });

    data.push({
      "Invoice No.": "FINAL TOTAL",
      "Order Date": "",
      "Sales Person Name": "",
      "Party Name": "",
      CGST: totals.cgst.toFixed(2),
      SGST: totals.sgst.toFixed(2),
      IGST: totals.igst.toFixed(2),
      "Basic Amount": totals.basic.toFixed(2),
      Tax: totals.tax.toFixed(2),
      Total: totals.grand.toFixed(2),
      Status: "",
    });

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  const exportPDF = async () => {
    const cols = visibleCols;
    const rowsForPdf = [];

    groupedRows.forEach((group) => {
      const monthLine = {};
      cols.forEach((c) => {
        if (c.key === "invoice") monthLine[c.header] = `${group.label} TOTAL`;
        else if (c.key === "cgst")
          monthLine[c.header] = group.totals.cgst.toFixed(2);
        else if (c.key === "sgst")
          monthLine[c.header] = group.totals.sgst.toFixed(2);
        else if (c.key === "igst")
          monthLine[c.header] = group.totals.igst.toFixed(2);
        else if (c.key === "basic")
          monthLine[c.header] = group.totals.basic.toFixed(2);
        else if (c.key === "tax")
          monthLine[c.header] = group.totals.tax.toFixed(2);
        else if (c.key === "grand")
          monthLine[c.header] = group.totals.grand.toFixed(2);
        else monthLine[c.header] = "";
      });
      rowsForPdf.push(monthLine);

      group.rows.forEach((r) => {
        const rowObj = {};
        cols.forEach((c) => {
          rowObj[c.header] = S(c.get(r)).replace(/"/g, '""');
        });
        rowsForPdf.push(rowObj);
      });
    });

    const finalLine = {};
    cols.forEach((c) => {
      if (c.key === "invoice") finalLine[c.header] = "FINAL TOTAL";
      else if (c.key === "cgst") finalLine[c.header] = totals.cgst.toFixed(2);
      else if (c.key === "sgst") finalLine[c.header] = totals.sgst.toFixed(2);
      else if (c.key === "igst") finalLine[c.header] = totals.igst.toFixed(2);
      else if (c.key === "basic") finalLine[c.header] = totals.basic.toFixed(2);
      else if (c.key === "tax") finalLine[c.header] = totals.tax.toFixed(2);
      else if (c.key === "grand") finalLine[c.header] = totals.grand.toFixed(2);
      else finalLine[c.header] = "";
    });
    rowsForPdf.push(finalLine);

    const header = cols.map((c) => c.header).join(",");
    const lines = rowsForPdf.map((r) =>
      cols.map((c) => `"${S(r[c.header]).replace(/"/g, '""')}"`).join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "SalesOrderReport");
  };

  const generateInvoicePDF = async (
    printData,
    details,
    allCharges,
    wordsNumber,
  ) => {
    try {
      const company = JSON.parse(localStorage.getItem("Companydetail") || "{}");
      const prefix = company?.Prefix || "";
      const suffix = company?.Suffix || "";
      const updated = {
        ...printData,
        invoiceId: `${prefix}${printData?.invoiceId || ""}${suffix}`,
      };

      let credit = 0,
        debit = 0,
        ledgerBalance = 0;
      let collected = {};

      if (updated?.partyId?._id) {
        const res = await VeiwLedgerBalance(updated.partyId._id);
        const ledger = res?.Ledger || [];
        ledger.forEach((l) =>
          l?.credit ? (credit += N(l.credit)) : (debit += N(l.debit)),
        );
        const diff = debit - credit;
        collected = {
          creditBalance: credit.toFixed(2),
          debitBalance: debit.toFixed(2),
          closingBalance: diff.toFixed(2),
        };
        ledgerBalance = diff;
      }

      const BilData = {
        CompanyDetails: company,
        BillNumber: updated?.invoiceId || "",
        PrintData: updated,
        Applied_Charges: allCharges || [],
        AllbillMerged: [],
        wordsNumber: wordsNumber || "",
        deliveryCharges: 0,
        otherCharges: 0,
        discount: 0,
        qr: "",
        upiid: "",
        totalTaxable: 0,
        ledgerBalance,
      };

      const blob = await pdf(
        <LastestInvoice
          invoiceData={updated}
          CollectedData={collected}
          BilData={BilData}
          tableList={details || []}
          AllCharges={allCharges || {}}
          fileName="invoice.pdf"
        />,
      ).toBlob();

      return blob;
    } catch (err) {
      console.error("generateInvoicePDF error:", err);
      return null;
    }
  };

  const onClickInvoice = async (row) => {
    try {
      const invoiceId =
        row?.invoiceId || row?.order?.invoiceId || row?.InvoiceId || "";
      if (!invoiceId) return swal("Invoice ID not found");

      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const resp = await _GetById(
        View_Invoice_Data_by_id,
        user?.database,
        invoiceId,
      );
      const printData = resp?.printData;
      if (!printData) return swal("Invoice data not found");

      const blob = await generateInvoicePDF(
        printData,
        printData?.orderItems || [],
        [],
        "",
      );
      if (!blob) return swal("Failed to generate PDF");

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      let partyName =
        printData?.partyId?.CompanyName || printData?.fullName || "Invoice";
      partyName = partyName.replace(/[^a-zA-Z0-9_-]/g, "_");
      link.href = url;
      link.download = `${partyName}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("Invoice download error:", e);
      swal("Something went wrong during download.");
    }
  };

  const handleParentSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    await loadData();
  };

  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !groupedRows?.length;

  return (
    <>
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      <Row className="so-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="so-filter-card">
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Sales Order Report{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              {masterShow ? (
                <div style={{ flex: "1 1 240px", maxWidth: 360 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              ) : (
                <div style={{ flex: "1 1 40px" }} />
              )}

              <div>
                <Label className="mb-0">Financial Year</Label>
                <Input
                  type="select"
                  value={fy}
                  onChange={(e) => setFy(e.target.value)}
                  style={{ minWidth: 160 }}
                >
                  {fyOptions.map((x) => (
                    <option key={x} value={x}>
                      {x}
                    </option>
                  ))}
                </Input>
              </div>

              <div>
                <Label className="mb-0">Filter Type</Label>
                <Input
                  type="select"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  style={{ minWidth: 150 }}
                >
                  <option value="MONTHLY">Monthly</option>
                  <option value="QUARTERLY">Quarterly</option>
                  <option value="HALF_YEARLY">Half Yearly</option>
                  <option value="YEARLY">Yearly</option>
                </Input>
              </div>

              {periodType === "MONTHLY" && (
                <div>
                  <Label className="mb-0">Month</Label>
                  <Input
                    type="select"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(Number(e.target.value))}
                    style={{ minWidth: 150 }}
                  >
                    {MONTHS_FY.map((m) => (
                      <option key={m.key} value={m.key}>
                        {m.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {periodType === "QUARTERLY" && (
                <div>
                  <Label className="mb-0">Quarter</Label>
                  <Input
                    type="select"
                    value={selectedQuarter}
                    onChange={(e) => setSelectedQuarter(e.target.value)}
                    style={{ minWidth: 170 }}
                  >
                    {QUARTERS.map((q) => (
                      <option key={q.value} value={q.value}>
                        {q.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              {periodType === "HALF_YEARLY" && (
                <div>
                  <Label className="mb-0">Half Year</Label>
                  <Input
                    type="select"
                    value={selectedHalf}
                    onChange={(e) => setSelectedHalf(e.target.value)}
                    style={{ minWidth: 170 }}
                  >
                    {HALVES.map((h) => (
                      <option key={h.value} value={h.value}>
                        {h.label}
                      </option>
                    ))}
                  </Input>
                </div>
              )}

              <div>
                <Label className="mb-0">
                  Select Status <span style={{ color: "red" }}>*</span>
                </Label>
                <Input
                  id="status-filter"
                  type="select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ minWidth: 220 }}
                >
                  <option value="">--Select--</option>
                  <option value="all">All Order</option>
                  <option value="pending">Pending</option>
                  <option value="billing">Billing Order</option>
                  <option value="dispatch">Dispatch Order</option>
                  <option value="pending for delivery">
                    Pending for Delivery
                  </option>
                  <option value="completed">Completed</option>
                  <option value="cancel in process">Cancel in process</option>
                  <option value="cancelled">Cancelled</option>
                </Input>
              </div>

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={exportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => exportXLSX("SalesOrderReport")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No records found.
              </div>
            ) : (
              <div className="so-table-wrap">
                <table className="so-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {groupedRows.map((group) => (
                      <React.Fragment key={group.key}>
                        <tr
                          className="so-month-row"
                          onClick={() => toggleMonth(group.key)}
                        >
                          {visibleCols.map((c, idx) => {
                            if (idx === 0) {
                              return (
                                <td key={c.key}>
                                  <div className="so-month-name">
                                    {monthExpanded[group.key] ? (
                                      <FaChevronDown />
                                    ) : (
                                      <FaChevronRight />
                                    )}
                                    <span>{group.label}</span>
                                  </div>
                                </td>
                              );
                            }

                            if (c.key === "cgst")
                              return (
                                <td key={c.key}>
                                  {group.totals.cgst.toFixed(2)}
                                </td>
                              );
                            if (c.key === "sgst")
                              return (
                                <td key={c.key}>
                                  {group.totals.sgst.toFixed(2)}
                                </td>
                              );
                            if (c.key === "igst")
                              return (
                                <td key={c.key}>
                                  {group.totals.igst.toFixed(2)}
                                </td>
                              );
                            if (c.key === "basic")
                              return (
                                <td key={c.key}>
                                  {group.totals.basic.toFixed(2)}
                                </td>
                              );
                            if (c.key === "tax")
                              return (
                                <td key={c.key}>
                                  {group.totals.tax.toFixed(2)}
                                </td>
                              );
                            if (c.key === "grand")
                              return (
                                <td key={c.key}>
                                  {group.totals.grand.toFixed(2)}
                                </td>
                              );
                            if (c.key === "party")
                              return <td key={c.key}>Month Total</td>;

                            return <td key={c.key}></td>;
                          })}
                        </tr>

                        {monthExpanded[group.key] &&
                          group.rows.map((r, i) => (
                            <tr
                              key={
                                r?._id || `${r?.invoiceId || r?.orderNo}-${i}`
                              }
                            >
                              {visibleCols.map((c) => (
                                <td key={c.key}>
                                  {typeof c.render === "function"
                                    ? c.render(r, onClickInvoice)
                                    : c.get(r)}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      {visibleCols.map((c, idx) => {
                        switch (c.key) {
                          case "cgst":
                            return (
                              <td key={c.key}>{totals.cgst.toFixed(2)}</td>
                            );
                          case "sgst":
                            return (
                              <td key={c.key}>{totals.sgst.toFixed(2)}</td>
                            );
                          case "igst":
                            return (
                              <td key={c.key}>{totals.igst.toFixed(2)}</td>
                            );
                          case "basic":
                            return (
                              <td key={c.key}>{totals.basic.toFixed(2)}</td>
                            );
                          case "tax":
                            return <td key={c.key}>{totals.tax.toFixed(2)}</td>;
                          case "grand":
                            return (
                              <td key={c.key}>{totals.grand.toFixed(2)}</td>
                            );
                          default:
                            return (
                              <td key={c.key}>
                                {idx === 0 ? "Final Total" : ""}
                              </td>
                            );
                        }
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div className="picklist">
                {BASE_COLS.map((col) => (
                  <div key={col.key} className="pickitem">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="picklist" style={{ flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        className="pickitem"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
